import { MenuItem, Select, Stack } from '@mui/material';
import i18next, { t } from 'i18next';
import { Check, ChevronDown } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { ThemeContext } from '../../Base';
import { getTextColorByRGB, hexToRgb } from '../../utils/color-utils';
import styles from './LanguageSelect.module.css';

const countryCodesAlpha3: { [key: string]: string } = {
  en: 'ENG',
  et: 'EST',
  lv: 'LVA',
  lt: 'LTU',
  fr: 'FRA',
  de: 'DEU',
  nl: 'NLD',
};

type LanguageSelectProps = {
  items: { label: string; value: string }[];
  value: string | undefined;
  onChange: (value: string) => void;
};

export const LanguageSelect = ({ items = [], value, onChange }: LanguageSelectProps) => {
  const theme = useContext(ThemeContext);
  const [lang, setLang] = useState(value);

  i18next.on('languageChanged', (lng) => {
    setLang(lng);
  });

  useEffect(() => {
    const rgb = hexToRgb(theme.backgroundColor);
    const color = getTextColorByRGB(rgb.r, rgb.g, rgb.b);
    document.body.style.setProperty('--select-foreground-color', color);
    document.body.style.setProperty('--select-border-color', color);
  }, [theme.backgroundColor]);

  return (
    <Select
      className={styles.select}
      value={lang}
      onChange={(e) => {
        localStorage.setItem('user_locale', 'true');
        setLang(e.target.value);
        onChange(e.target.value);
      }}
      renderValue={(selectedValue) => countryCodesAlpha3[selectedValue]}
      IconComponent={ChevronDown}
      MenuProps={{
        className: styles.menu,
      }}
      SelectDisplayProps={{
        'aria-label': t('form.main.selectLanguage'),
      }}
      {...{ 'data-testid': 'languageSwitcher' }}
    >
      {items.map((item) => (
        <MenuItem className={styles.item} key={item.value} value={item.value} lang={item.value}>
          <Stack
            direction="row"
            sx={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}>
            {item.label}
            {value === item.value && <Check></Check>}
          </Stack>
        </MenuItem>
      ))}
    </Select>
  );
};
